// Portfolio: holdings worked out from recorded trades (Claude records them through the connector).
import { api } from "./api";
import { riskWord, starsHtml } from "./assess";
import { sparkline } from "./charts";
import { pendingTradesCard } from "./inbox";
import { codePill, date, dateTime, dayLabel, esc, fixed, money, pct, price, stockLink, tone } from "./format";

export interface Holding {
  code: string; units: number; avgCost: number; costBase: number; realised: number; firstBought: string;
  price: number | null; value: number | null; unrealised: number | null; unrealisedPct: number | null; dayChangePct: number | null; priceDate?: string | null; weight: number | null;
}
interface Trade { id: number; code: string; side: string; units: number; price: number; fees: number; tradedOn: string; note: string | null }
export interface Portfolio {
  holdings: Holding[];
  totals: { value: number; costBase: number; unrealised: number; unrealisedPct: number | null; realised: number };
  trades: Trade[];
  history: { day: string; value: number; costBase: number; unrealised: number }[];
  refreshed: { at: string; trigger: string } | null;
  ratings: Record<string, { health: number; risk: number; reward: number; assessedAt: string }>;
}

let cached: Promise<Portfolio> | null = null;
export const loadPortfolio = (fresh = false) => {
  if (fresh || !cached) cached = api<Portfolio>("portfolio").catch((e) => { cached = null; throw e; });
  return cached;
};

const signedMoney = (v: number | null) => (v == null ? "–" : (v > 0 ? "+" : "") + money(v));

export async function renderPortfolio(app: HTMLElement) {
  app.innerHTML = `<section class="portfolio"><h2>Portfolio</h2><div id="pf-email"></div><div id="pf"><div class="loading" role="status"><span></span>Loading…</div></div></section>`;
  const el = document.getElementById("pf")!;
  pendingTradesCard(document.getElementById("pf-email")!, () => renderPortfolio(app));
  let p: Portfolio;
  try {
    p = await loadPortfolio(true);
  } catch (e) {
    el.innerHTML = `<div class="notice error">${esc((e as Error).message)}</div>`;
    return;
  }
  if (!p.trades.length) {
    el.innerHTML = `<section class="card"><h3>No trades yet</h3>
      <p>Your holdings are worked out from your trades. The easiest way to add them: in your Claude app with the Gold connector on, paste or attach your CommSec transaction history (or a trade confirmation) and ask Claude to record the trades in Gold.</p>
      <p class="muted small">Set up the connector from the Gold home page.</p></section>`;
    return;
  }
  const t = p.totals;
  // Today's move counts only stocks priced today; the rest haven't traded yet today (or the delayed
  // feed hasn't caught up), which is zero change, as CommSec shows it.
  const isToday = (h: Holding) => dayLabel(h.priceDate) === "today";
  const day = p.holdings.reduce((a, h) => a + (isToday(h) && h.value != null && h.dayChangePct != null ? h.value - h.value / (1 + h.dayChangePct) : 0), 0);
  const waiting = p.holdings.filter((h) => !isToday(h)).map((h) => h.code);
  el.innerHTML = `
    <div class="grid">
      <section class="card"><dl class="stats">
        <div class="stat"><dt>Market value</dt><dd class="big">${money(t.value)}</dd></div>
        <div class="stat"><dt>Today</dt><dd class="${tone(day)}">${signedMoney(day)}${waiting.length ? `<small class="muted"> ${esc(waiting.join(", "))} not traded yet</small>` : ""}</dd></div>
        <div class="stat"><dt>Cost base</dt><dd>${money(t.costBase)}</dd></div>
        <div class="stat"><dt>Unrealised</dt><dd class="${tone(t.unrealised)}">${signedMoney(t.unrealised)} <small>${pct(t.unrealisedPct, true)}</small></dd></div>
        <div class="stat"><dt>Realised (all time)</dt><dd class="${tone(t.realised)}">${signedMoney(t.realised)}</dd></div>
        <div class="stat"><dt>Holdings</dt><dd>${p.holdings.length}</dd></div>
      </dl>
      <div class="alloc">${p.holdings.map((h, i) => (h.weight ? `<span class="a${i % 6}" style="flex:${h.weight}" title="${esc(h.code)} ${pct(h.weight)}"></span>` : "")).join("")}</div>
      <p class="muted small">${p.holdings.map((h, i) => `<i class="key a${i % 6}"></i>${stockLink(h.code, esc(h.code))} ${pct(h.weight)}`).join(" · ")}</p>
      </section>
    </div>
    ${p.history.length >= 2 ? (() => {
      const first = p.history[0], last = p.history.at(-1)!;
      const ch = first.value ? last.value / first.value - 1 : null;
      return `<section class="card"><h3>Value at each close</h3>
        <div class="spark-wrap">${sparkline(p.history.map((h) => h.value))}</div>
        <p class="muted small">${date(first.day)}: ${money(first.value)} → ${date(last.day)}: ${money(last.value)} <b class="${tone(ch)}">${pct(ch, true)}</b>. Includes the effect of buying and selling.</p></section>`;
    })() : ""}
    <ul class="holdings">${p.holdings.map((h) => `
      <li><a href="#/${esc(h.code)}" data-code="${esc(h.code)}">
        <b>${codePill(h.code)}${p.ratings?.[h.code] ? `<span class="hr" title="Investment Health ${p.ratings[h.code].health}★ · ${riskWord(p.ratings[h.code].risk)} · assessed ${date(p.ratings[h.code].assessedAt)}">${starsHtml(p.ratings[h.code].health)}</span>` : ""}</b>
        <span class="muted small">${fixed(h.units, h.units % 1 ? 2 : 0)} @ ${price(h.avgCost)}</span>
        <span class="hv">${money(h.value)}<small class="${tone(h.dayChangePct)}">${h.value != null && h.dayChangePct != null ? `${signedMoney(h.value - h.value / (1 + h.dayChangePct))} ` : ""}${pct(h.dayChangePct, true)} ${dayLabel(h.priceDate)}</small></span>
        <span class="hp ${tone(h.unrealised)}">${signedMoney(h.unrealised)} <small>${pct(h.unrealisedPct, true)}</small></span>
      </a></li>`).join("")}</ul>
    <section class="card"><h3>Trades</h3><div class="table-wrap"><table>
      <thead><tr><th>Date</th><th>Stock</th><th>Side</th><th>Units</th><th>Price</th><th>Fees</th></tr></thead>
      <tbody>${p.trades.map((tr) => `<tr><td>${date(tr.tradedOn)}</td><td>${stockLink(tr.code)}</td>
        <td class="${tr.side === "buy" ? "up" : "down"}">${tr.side === "buy" ? "Buy" : "Sell"}</td><td>${fixed(tr.units, tr.units % 1 ? 2 : 0)}</td><td>${price(tr.price)}</td><td>${price(tr.fees)}</td></tr>`).join("")}</tbody>
    </table></div><p class="muted small">Average-cost method, brokerage included in the cost base. Prices refresh automatically every 30 minutes while the ASX is open, and the closing value is saved each trading day${p.refreshed ? ` (last refresh ${dateTime(p.refreshed.at)}${p.refreshed.trigger === "button" ? ", by you" : ""})` : ""}. Tap ↻ to refresh now. To fix a trade, ask Claude to delete or re-record it.</p></section>`;
}
